import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useFocusTrap } from '@/hooks/useFocusTrap';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  variant?: 'modal' | 'dropdown';
  className?: string;
  style?: React.CSSProperties;
}

export function Modal({ isOpen, onClose, children, variant = 'modal', className = '', style = {} }: ModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isOpen) {
      if (variant === 'modal') {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
        document.body.style.overflow = 'hidden';
      }
    } else {
      if (variant === 'modal') {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    }
  }, [isOpen, variant]);

  useEffect(() => {
    return () => {
      if (variant === 'modal') {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    };
  }, [variant]);

  useFocusTrap(containerRef as React.RefObject<HTMLElement | null>, isOpen);

  const isModal = variant === 'modal';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {isModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 z-[99998] bg-slate-900/50 backdrop-blur-sm hidden sm:block"
            />
          )}
          {isModal ? (
            <div className="fixed inset-0 z-[99999] hidden sm:flex items-center justify-center p-4 pointer-events-none">
              <motion.div
                ref={containerRef}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                transition={{ type: "spring", damping: 25, stiffness: 300, mass: 0.8 }}
                tabIndex={-1}
                style={style}
                className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl dark:shadow-xl w-full max-h-[90vh] overflow-hidden pointer-events-auto flex flex-col ${className}`}
              >
                {children}
              </motion.div>
            </div>
          ) : (
            <motion.div
              ref={containerRef}
              initial={{ opacity: 0, y: 20, scale: 1 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 1 }}
              transition={{ duration: 0.2 }}
              style={style}
              tabIndex={-1}
              className={`absolute hidden sm:flex top-full ${Object.keys(style).length === 0 ? 'right-0' : ''} mt-2 z-[99999] min-w-[12rem] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl rounded-xl origin-top-right flex-col max-h-[60vh] overflow-hidden ${className}`}
            >
              {children}
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
}
